import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_ALL_INQUIRIES } from '../../graphql/queries';
import type { AdminInquiriesListResult } from '../../types';
import { Spinner } from '../common/Spinner';
import InquiryRow from './inquiry/InquiryRow';
import InquiryRowExpanded from './inquiry/InquiryRowExpanded';

const PAGE_SIZE = 25;
const COLUMN_COUNT = 4;

type ActiveTab = 'unanswered' | 'completed';

interface GetAllInquiriesResponse {
  getAllInquiries: AdminInquiriesListResult;
}

/**
 * Admin-side 1:1 inquiry management page at `/admin/inquiries`.
 *
 * Owns tab state (`미답변` / `완료`), paginated navigation, and row expansion,
 * and wires the list + counts to `getAllInquiries`. When a reply is posted
 * via an expanded row's InlineReplyForm, this component resets pagination
 * to the first page, clears the active row, refetches the list, and fires
 * a transient success toast.
 */
const AdminInquiryListPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ActiveTab>('unanswered');
  // Stack of tokens used to reach previous pages. We use '' as the sentinel
  // for "first page / no token" so this can still typecheck as string[] per
  // the spec. The Apollo variable falls back to undefined when the slot is
  // empty.
  const [tokenStack, setTokenStack] = useState<string[]>([]);
  const [currentToken, setCurrentToken] = useState<string>('');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);

  // Reset pagination + active row whenever the active tab changes.
  useEffect(() => {
    setTokenStack([]);
    setCurrentToken('');
    setActiveRowId(null);
  }, [activeTab]);

  const { data, loading, error, refetch } = useQuery<GetAllInquiriesResponse>(
    gql`${GET_ALL_INQUIRIES}`,
    {
      variables: {
        filter: { answered: activeTab === 'completed' },
        limit: PAGE_SIZE,
        nextToken: currentToken || undefined,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const result = data?.getAllInquiries;
  const inquiries = result?.inquiries ?? [];
  const unansweredCount = result?.unansweredCount ?? 0;
  const answeredCount = result?.answeredCount ?? 0;
  const activeTabCount = activeTab === 'unanswered' ? unansweredCount : answeredCount;
  const nextToken = result?.nextToken ?? null;

  const canGoPrev = currentToken !== '' || tokenStack.length > 0;
  const canGoNext = Boolean(nextToken);

  const handlePrev = () => {
    if (!canGoPrev) return;
    const newStack = [...tokenStack];
    const previous = newStack.pop() ?? '';
    setTokenStack(newStack);
    setCurrentToken(previous);
    setActiveRowId(null);
  };

  const handleNext = () => {
    if (!canGoNext || !nextToken) return;
    setTokenStack((stack) => [...stack, currentToken]);
    setCurrentToken(nextToken);
    setActiveRowId(null);
  };

  const handleTabChange = (tab: ActiveTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    // tokenStack, currentToken, activeRowId reset via useEffect above.
  };

  const handleToggleRow = (inquiryId: string) => {
    setActiveRowId((current) => (current === inquiryId ? null : inquiryId));
  };

  const handleReplySuccess = async () => {
    // Per spec: after a successful reply, reset to the first page of the
    // currently active tab and refetch the list + counts.
    setTokenStack([]);
    setCurrentToken('');
    setActiveRowId(null);
    toast.success(t('inquiries.toast.success') as string);
    try {
      await refetch({
        filter: { answered: activeTab === 'completed' },
        limit: PAGE_SIZE,
        nextToken: undefined,
      });
    } catch (err) {
      // Refetch errors are non-fatal for the UX — Apollo will surface an
      // error banner on the next render if needed.
      // eslint-disable-next-line no-console
      console.warn('[admin] refetch after reply failed', err);
    }
  };

  const emptyMessage =
    activeTab === 'unanswered'
      ? (t('inquiries.empty.unanswered') as string)
      : (t('inquiries.empty.completed') as string);

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('admin:sidebar.customerService')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{t('admin:sidebar.inquiries')}</span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin:inquiries.title')}</h1>

        {/* Tab bar */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {(
            [
              {
                key: 'unanswered' as ActiveTab,
                label: t('admin:inquiries.tabs.unanswered', { count: unansweredCount }) as string,
              },
              {
                key: 'completed' as ActiveTab,
                label: t('admin:inquiries.tabs.completed', { count: answeredCount }) as string,
              },
            ]
          ).map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Global error banner (network / permission). */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error.message || (t('admin:inquiries.error.loadFailed') as string)}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:inquiries.columns.name')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:inquiries.columns.userType')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:inquiries.columns.content')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:inquiries.columns.createdAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && inquiries.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="px-4 py-16 text-center">
                    <Spinner size="medium" />
                  </td>
                </tr>
              ) : activeTabCount === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                inquiries.map((inq) => {
                  const isActive = activeRowId === inq.inquiryId;
                  return (
                    <React.Fragment key={inq.inquiryId}>
                      <InquiryRow
                        inquiry={inq}
                        isActive={isActive}
                        onToggle={handleToggleRow}
                      />
                      {isActive && (
                        <InquiryRowExpanded
                          inquiry={inq}
                          colSpan={COLUMN_COUNT}
                          onReplySuccess={handleReplySuccess}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {activeTabCount > 0 && (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handlePrev}
              disabled={!canGoPrev || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
            >
              {t('admin:inquiries.pagination.prev')}
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
            >
              {t('admin:inquiries.pagination.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiryListPage;
